import React from 'react';
import { cn } from '@/lib/utils';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, icon, className, required, id, ...props }, ref) => {
    const inputId = id || React.useId();

    const baseClasses =
      'w-full px-4 py-2.5 bg-white border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 disabled:bg-gray-50 disabled:cursor-not-allowed placeholder-gray-400 text-sm min-h-[40px]';

    const stateClasses = error
      ? 'border-error/50 text-error focus:ring-error/30'
      : 'border-gray-200 text-gray-900 hover:border-gray-300';

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1.5">
            {label}
            {required && <span className="text-error ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              {icon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(baseClasses, stateClasses, icon && 'pl-10', className)}
            aria-invalid={error ? 'true' : 'false'}
            aria-required={required}
            required={required}
            {...props}
          />
        </div>

        {error && (
          <p className="mt-1.5 text-sm text-error font-medium" role="alert">
            {error}
          </p>
        )}

        {helperText && !error && (
          <p className="mt-1.5 text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
export default Input;
